import React, { useState, useEffect, useRef } from "react";
import { useI18n } from "@/contexts/I18nContext";
import {
  Briefcase, Clock, Award, MessageCircle, Gift,
  Pencil, MousePointer2, Eraser, Undo2,
  TrendingUp, TrendingDown, BarChart3, X, Send,
  ChevronRight, CheckCircle2, AlertCircle, Tag, Plus
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CHART_TOOLS = [
  { id: "cursor", icon: MousePointer2 },
  { id: "draw", icon: Pencil },
  { id: "erase", icon: Eraser },
  { id: "undo", icon: Undo2 },
];

// Generate stable random sentiment per symbol using a seeded hash
function getSymbolSentiment(symbol) {
  // Use symbol characters to create a deterministic but varied seed
  let hash = 0;
  for (let i = 0; i < symbol.length; i++) {
    hash = (hash * 31 + symbol.charCodeAt(i)) & 0xffffffff;
  }
  // Base sentiment ranges per symbol type
  const baseUp = 42 + (Math.abs(hash) % 30); // 42-71%
  return { up: baseUp, down: 100 - baseUp };
}

// Dynamically fluctuating sentiment using a live random walk
function useDynamicSentiment(symbol) {
  const base = getSymbolSentiment(symbol || "BTC");
  const [sentiment, setSentiment] = useState(base);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!symbol) return;
    const b = getSymbolSentiment(symbol);
    setSentiment(b);

    // Update every 4-8 seconds with small fluctuations
    const update = () => {
      const interval = 4000 + Math.random() * 4000;
      timerRef.current = setTimeout(() => {
        setSentiment(prev => {
          const delta = (Math.random() - 0.5) * 6; // ±3%
          const newUp = Math.max(30, Math.min(75, prev.up + delta));
          return { up: Math.round(newUp), down: Math.round(100 - newUp) };
        });
        update();
      }, interval);
    };
    update();

    return () => clearTimeout(timerRef.current);
  }, [symbol]);

  return sentiment;
}

// ─── Support Panel ───
function SupportPanel({ onClose }) {
  const [tickets, setTickets] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list"); // list | new

  const loadTickets = async () => {
    try {
      const { data } = await api.get("/support/tickets/my");
      setTickets(data);
    } catch {}
  };

  useEffect(() => { loadTickets(); }, []);

  const createTicket = async () => {
    if (!subject.trim() || !message.trim()) return;
    setLoading(true);
    try {
      await api.post("/support/tickets", { subject, message, category });
      toast.success("Destek talebiniz oluşturuldu!");
      setSubject(""); setMessage(""); setView("list");
      loadTickets();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const statusColor = (s) => s === "open" ? "var(--iq-orange)" : s === "answered" ? "var(--iq-green)" : "#888";
  const statusLabel = (s) => s === "open" ? "Açık" : s === "answered" ? "Yanıtlandı" : "Kapatıldı";

  return (
    <SidePanel title="Destek" onClose={onClose}>
      {view === "list" ? (
        <>
          <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--iq-border)" }}>
            <button
              onClick={() => setView("new")}
              style={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "8px 12px", background: "rgba(0,230,118,0.1)", border: "1px solid rgba(0,230,118,0.3)",
                borderRadius: 6, color: "var(--iq-green)", cursor: "pointer", fontSize: 12, fontWeight: 700,
              }}
            >
              <Plus style={{ width: 14, height: 14 }} />
              Yeni Talep Oluştur
            </button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "8px" }}>
            {tickets.length === 0 && (
              <div style={{ textAlign: "center", color: "var(--iq-text-dim)", fontSize: 12, padding: "32px 0" }}>
                Henüz destek talebiniz yok
              </div>
            )}
            {tickets.map(t => (
              <div key={t.id} style={{
                background: "var(--iq-bg)", border: "1px solid var(--iq-border)", borderRadius: 6,
                padding: "10px", marginBottom: 6,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", flex: 1, marginRight: 8 }}>{t.subject}</div>
                  <span style={{ fontSize: 9, color: statusColor(t.status), textTransform: "uppercase", fontWeight: 700, flexShrink: 0 }}>
                    {statusLabel(t.status)}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: "var(--iq-text-dim)", marginBottom: 6, lineHeight: 1.4 }}>{t.message.slice(0, 80)}...</div>
                {t.replies?.length > 0 && (
                  <div style={{ background: "rgba(0,230,118,0.06)", border: "1px solid rgba(0,230,118,0.15)", borderRadius: 4, padding: "6px 8px" }}>
                    <div style={{ fontSize: 9, color: "var(--iq-green)", fontWeight: 700, marginBottom: 3 }}>YANIT</div>
                    <div style={{ fontSize: 11, color: "var(--iq-text-bright)" }}>{t.replies[t.replies.length - 1].message}</div>
                  </div>
                )}
                <div style={{ fontSize: 9, color: "var(--iq-text-dim)", marginTop: 4 }}>
                  {new Date(t.created_at).toLocaleDateString("tr-TR")}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "12px" }}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 9, color: "var(--iq-text-dim)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>Kategori</div>
            <select
              value={category} onChange={e => setCategory(e.target.value)}
              style={{ width: "100%", background: "var(--iq-bg)", border: "1px solid var(--iq-border-bright)", borderRadius: 5, color: "#fff", padding: "7px 8px", fontSize: 12, outline: "none" }}
            >
              <option value="general">Genel</option>
              <option value="deposit">Yatırım</option>
              <option value="withdrawal">Çekim</option>
              <option value="trade">İşlem</option>
              <option value="technical">Teknik</option>
            </select>
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 9, color: "var(--iq-text-dim)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>Konu</div>
            <input
              value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="Sorununuzu kısaca belirtin..."
              style={{ width: "100%", background: "var(--iq-bg)", border: "1px solid var(--iq-border-bright)", borderRadius: 5, color: "#fff", padding: "7px 8px", fontSize: 12, outline: "none", boxSizing: "border-box" }}
            />
          </div>
          <div style={{ marginBottom: 10, flex: 1 }}>
            <div style={{ fontSize: 9, color: "var(--iq-text-dim)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>Mesaj</div>
            <textarea
              value={message} onChange={e => setMessage(e.target.value)}
              placeholder="Sorununuzu ayrıntılı açıklayın..."
              rows={4}
              style={{ width: "100%", background: "var(--iq-bg)", border: "1px solid var(--iq-border-bright)", borderRadius: 5, color: "#fff", padding: "7px 8px", fontSize: 12, outline: "none", resize: "none", boxSizing: "border-box", fontFamily: "inherit" }}
            />
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setView("list")} style={{ flex: 1, padding: "8px", background: "transparent", border: "1px solid var(--iq-border-bright)", borderRadius: 5, color: "var(--iq-text)", cursor: "pointer", fontSize: 12 }}>İptal</button>
            <button
              onClick={createTicket} disabled={loading}
              style={{ flex: 2, padding: "8px", background: "rgba(0,230,118,0.15)", border: "1px solid rgba(0,230,118,0.4)", borderRadius: 5, color: "var(--iq-green)", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: loading ? 0.6 : 1 }}
            >
              <Send style={{ width: 12, height: 12 }} />
              Gönder
            </button>
          </div>
        </div>
      )}
    </SidePanel>
  );
}

// ─── Promo Panel ───
function PromoPanel({ onClose }) {
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    api.get("/support/promos").then(({ data }) => setPromos(data)).catch(() => {});
  }, []);

  const typeLabel = (t) => ({ bonus: "Bonus", cashback: "Cashback", free_trade: "Ücretsiz İşlem", deposit_match: "Yatırım Eşleştirme" })[t] || t;
  const typeColor = (t) => ({ bonus: "var(--iq-green)", cashback: "var(--iq-blue)", free_trade: "var(--iq-orange)", deposit_match: "#a855f7" })[t] || "#888";

  return (
    <SidePanel title="Promosyonlar" onClose={onClose}>
      <div style={{ flex: 1, overflowY: "auto", padding: "8px" }}>
        {promos.length === 0 && (
          <div style={{ textAlign: "center", color: "var(--iq-text-dim)", fontSize: 12, padding: "32px 0" }}>
            <Gift style={{ width: 32, height: 32, margin: "0 auto 8px", display: "block", opacity: 0.4 }} />
            Şu an aktif promosyon yok
          </div>
        )}
        {promos.map(p => (
          <div key={p.id} style={{
            background: "linear-gradient(135deg, var(--iq-bg), rgba(255,255,255,0.02))",
            border: `1px solid ${typeColor(p.promo_type)}33`,
            borderRadius: 8, padding: "12px", marginBottom: 8,
            boxShadow: `0 2px 12px ${typeColor(p.promo_type)}11`,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{p.title}</div>
              <span style={{
                fontSize: 9, color: typeColor(p.promo_type), background: `${typeColor(p.promo_type)}22`,
                border: `1px solid ${typeColor(p.promo_type)}44`, borderRadius: 3,
                padding: "2px 5px", fontWeight: 700, textTransform: "uppercase", flexShrink: 0,
              }}>
                {typeLabel(p.promo_type)}
              </span>
            </div>
            <div style={{ fontSize: 11, color: "var(--iq-text)", marginBottom: 8, lineHeight: 1.5 }}>{p.description}</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: typeColor(p.promo_type), fontFamily: "'JetBrains Mono', monospace" }}>
                +{p.value}{p.promo_type === "cashback" || p.promo_type === "deposit_match" ? "%" : "$"}
              </div>
              {p.min_deposit > 0 && (
                <div style={{ fontSize: 10, color: "var(--iq-text-dim)" }}>Min. ${p.min_deposit} yatırım</div>
              )}
            </div>
            {p.expires_at && (
              <div style={{ fontSize: 9, color: "var(--iq-text-dim)", marginTop: 6 }}>
                Son geçerlilik: {new Date(p.expires_at).toLocaleDateString("tr-TR")}
              </div>
            )}
          </div>
        ))}
      </div>
    </SidePanel>
  );
}

// ─── Portfolio Panel ───
function PortfolioPanel({ onClose, openTrades, history }) {
  const { t } = useI18n();
  const totalOpen = (openTrades || []).reduce((s, t) => s + Number(t.amount), 0);
  const winCount = (history || []).filter(t => Number(t.pnl) > 0).length;
  const loseCount = (history || []).filter(t => Number(t.pnl) <= 0).length;
  const totalPnl = (history || []).reduce((s, t) => s + Number(t.pnl || 0), 0);

  return (
    <SidePanel title={t("portfolio")} onClose={onClose}>
      <div style={{ padding: "12px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
          {[
            { label: t("open_trades"), value: openTrades?.length || 0, color: "var(--iq-orange)" },
            { label: t("active_investment"), value: `$${totalOpen.toFixed(0)}`, color: "var(--iq-blue)" },
            { label: t("won"), value: winCount, color: "var(--iq-green)" },
            { label: t("lost"), value: loseCount, color: "var(--iq-red)" },
          ].map(item => (
            <div key={item.label} style={{ background: "var(--iq-bg)", border: "1px solid var(--iq-border)", borderRadius: 6, padding: "10px 8px" }}>
              <div style={{ fontSize: 9, color: "var(--iq-text-dim)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>{item.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: item.color, fontFamily: "'JetBrains Mono', monospace" }}>{item.value}</div>
            </div>
          ))}
        </div>
        <div style={{
          background: totalPnl >= 0 ? "rgba(0,230,118,0.08)" : "rgba(255,23,68,0.08)",
          border: `1px solid ${totalPnl >= 0 ? "rgba(0,230,118,0.2)" : "rgba(255,23,68,0.2)"}`,
          borderRadius: 8, padding: "12px", textAlign: "center",
        }}>
          <div style={{ fontSize: 9, color: "var(--iq-text-dim)", textTransform: "uppercase", marginBottom: 4 }}>Toplam P&L</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: totalPnl >= 0 ? "var(--iq-green)" : "var(--iq-red)", fontFamily: "'JetBrains Mono', monospace" }}>
            {totalPnl >= 0 ? "+" : ""}${totalPnl.toFixed(2)}
          </div>
        </div>
      </div>
    </SidePanel>
  );
}

// ─── History Panel ───
function HistoryPanel({ onClose, history }) {
  return (
    <SidePanel title="İşlem Geçmişi" onClose={onClose}>
      <div style={{ flex: 1, overflowY: "auto", padding: "8px" }}>
        {(!history || history.length === 0) && (
          <div style={{ textAlign: "center", color: "var(--iq-text-dim)", fontSize: 12, padding: "32px 0" }}>İşlem geçmişi yok</div>
        )}
        {(history || []).slice(0, 30).map(t => (
          <div key={t.id} style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "var(--iq-bg)", border: "1px solid var(--iq-border)",
            borderRadius: 6, padding: "8px", marginBottom: 4,
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
              background: Number(t.pnl) > 0 ? "rgba(0,230,118,0.15)" : "rgba(255,23,68,0.15)",
              border: `1px solid ${Number(t.pnl) > 0 ? "rgba(0,230,118,0.3)" : "rgba(255,23,68,0.3)"}`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {Number(t.pnl) > 0
                ? <TrendingUp style={{ width: 13, height: 13, color: "var(--iq-green)" }} />
                : <TrendingDown style={{ width: 13, height: 13, color: "var(--iq-red)" }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#fff" }}>{t.symbol}</div>
              <div style={{ fontSize: 9, color: "var(--iq-text-dim)" }}>{new Date(t.closed_at || t.created_at).toLocaleString("tr-TR")}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: Number(t.pnl) > 0 ? "var(--iq-green)" : "var(--iq-red)" }}>
                {Number(t.pnl) > 0 ? "+" : ""}${Number(t.pnl || 0).toFixed(2)}
              </div>
              <div style={{ fontSize: 9, color: "var(--iq-text-dim)" }}>${Number(t.amount).toFixed(0)}</div>
            </div>
          </div>
        ))}
      </div>
    </SidePanel>
  );
}

// ─── Results Panel ───
function ResultsPanel({ onClose, history }) {
  const wins = (history || []).filter(t => Number(t.pnl) > 0);
  const loses = (history || []).filter(t => Number(t.pnl) <= 0);
  const winRate = history?.length ? ((wins.length / history.length) * 100).toFixed(1) : 0;
  const bestTrade = wins.reduce((max, t) => Number(t.pnl) > Number(max?.pnl || 0) ? t : max, null);

  return (
    <SidePanel title="Sonuçlarım" onClose={onClose}>
      <div style={{ padding: "12px" }}>
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 9, color: "var(--iq-text-dim)", textTransform: "uppercase", marginBottom: 4 }}>Kazanma Oranı</div>
          <div style={{
            fontSize: 40, fontWeight: 900, fontFamily: "'JetBrains Mono', monospace",
            color: winRate >= 50 ? "var(--iq-green)" : "var(--iq-red)",
          }}>{winRate}%</div>
          <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 6 }}>
            <span style={{ fontSize: 11, color: "var(--iq-green)" }}>✓ {wins.length} Kazanç</span>
            <span style={{ fontSize: 11, color: "var(--iq-red)" }}>✗ {loses.length} Kayıp</span>
          </div>
        </div>
        {/* Win rate bar */}
        <div style={{ height: 6, background: "rgba(255,23,68,0.3)", borderRadius: 3, marginBottom: 12, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${winRate}%`, background: "var(--iq-green)", borderRadius: 3, transition: "width 1s ease" }} />
        </div>
        {bestTrade && (
          <div style={{ background: "rgba(0,230,118,0.07)", border: "1px solid rgba(0,230,118,0.2)", borderRadius: 6, padding: "10px" }}>
            <div style={{ fontSize: 9, color: "var(--iq-green)", textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>🏆 En İyi İşlem</div>
            <div style={{ fontSize: 12, color: "#fff", fontWeight: 600 }}>{bestTrade.symbol}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "var(--iq-green)", fontFamily: "'JetBrains Mono', monospace" }}>
              +${Number(bestTrade.pnl).toFixed(2)}
            </div>
          </div>
        )}
      </div>
    </SidePanel>
  );
}

// ─── Side Panel Wrapper ───
function SidePanel({ title, onClose, children }) {
  return (
    <div style={{
      position: "absolute", left: "var(--iq-sidebar-w)", top: 0, bottom: 0,
      width: 280, zIndex: 100,
      background: "var(--iq-panel)",
      borderRight: "1px solid var(--iq-border-bright)",
      display: "flex", flexDirection: "column",
      boxShadow: "4px 0 24px rgba(0,0,0,0.5)",
      animation: "slideInLeft 0.2s cubic-bezier(0.34,1.56,0.64,1)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 14px", borderBottom: "1px solid var(--iq-border)",
        background: "linear-gradient(90deg, rgba(0,230,118,0.05), transparent)",
        flexShrink: 0,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#fff" }}>{title}</div>
        <button onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--iq-text-dim)", cursor: "pointer", padding: 4, display: "flex" }}>
          <X style={{ width: 14, height: 14 }} />
        </button>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>{children}</div>
    </div>
  );
}

export default function Sidebar({ onMenuClick, activeMenu, symbol, openTrades, history }) {
  const { t } = useI18n();
  const MENU_ITEMS = [
    { id: "portfolio", icon: Briefcase, label: t("total_portfolio") },
    { id: "history", icon: Clock, label: t("trade_history") },
    { id: "results", icon: Award, label: t("my_results"), badge: "NEW" },
    { id: "support", icon: MessageCircle, label: t("chat_support") },
    { id: "promo", icon: Gift, label: t("promotions"), badge: "2" },
  ];
  const sentiment = useDynamicSentiment(symbol);

  const handleMenu = (id) => {
    onMenuClick?.(id);
  };

  return (
    <div style={{ position: "relative" }}>
      <div className="iq-sidebar" data-testid="iq-sidebar">
        {/* Sentiment bars */}
        <div style={{ padding: "8px 4px", borderBottom: "1px solid var(--iq-border)" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "2px", width: "100%" }}>
              <TrendingUp style={{ width: 10, height: 10, color: "var(--iq-green)" }} />
              <span style={{ fontSize: "8px", color: "var(--iq-green)", fontWeight: 700 }}>{t("high")}</span>
            </div>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--iq-green)" }}>{sentiment.up}%</div>
            <div style={{ width: "100%", height: "40px", display: "flex", flexDirection: "column", borderRadius: "2px", overflow: "hidden" }}>
              <div style={{ flex: sentiment.up, background: "var(--iq-green)", transition: "flex 0.8s ease" }} />
              <div style={{ flex: sentiment.down, background: "#ef4444", transition: "flex 0.8s ease" }} />
            </div>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#ef4444" }}>{sentiment.down}%</div>
            <div style={{ display: "flex", alignItems: "center", gap: "2px", width: "100%" }}>
              <TrendingDown style={{ width: 10, height: 10, color: "#ef4444" }} />
              <span style={{ fontSize: "8px", color: "#ef4444", fontWeight: 700 }}>{t("down")}</span>
            </div>
          </div>
        </div>

        {/* Menu items */}
        <div style={{ flex: 1 }}>
          {MENU_ITEMS.map((item) => (
            <button
              key={item.id}
              data-testid={`sidebar-${item.id}`}
              className={`iq-sidebar-btn ${activeMenu === item.id ? "active" : ""}`}
              onClick={() => handleMenu(item.id)}
            >
              {item.badge && <span className="badge">{item.badge}</span>}
              <item.icon style={{ width: 18, height: 18 }} />
              <span style={{ fontSize: "7px", lineHeight: 1.2, whiteSpace: "pre-line" }}>{item.label}</span>
            </button>
          ))}
        </div>

        {/* Chart drawing tools */}
        <div style={{ borderTop: "1px solid var(--iq-border)", paddingTop: "4px" }}>
          {CHART_TOOLS.map((tool) => (
            <button
              key={tool.id}
              className="iq-sidebar-btn"
              data-testid={`tool-${tool.id}`}
              style={{ padding: "6px 4px" }}
            >
              <tool.icon style={{ width: 16, height: 16 }} />
            </button>
          ))}
        </div>

        {/* Timeframe indicator */}
        <div style={{ borderTop: "1px solid var(--iq-border)", padding: "6px 4px", textAlign: "center" }}>
          <div style={{ fontSize: "11px", fontWeight: 600, color: "#fff" }}>2d</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "1px", marginTop: "4px" }}>
            <div style={{ fontSize: "7px", color: "var(--iq-text-dim)" }}>
              <BarChart3 style={{ width: 10, height: 10, display: "inline" }} />
            </div>
          </div>
        </div>
      </div>

      {/* Side panel overlays */}
      {activeMenu === "support" && <SupportPanel onClose={() => onMenuClick?.(null)} />}
      {activeMenu === "promo" && <PromoPanel onClose={() => onMenuClick?.(null)} />}
      {activeMenu === "portfolio" && <PortfolioPanel onClose={() => onMenuClick?.(null)} openTrades={openTrades} history={history} />}
      {activeMenu === "history" && <HistoryPanel onClose={() => onMenuClick?.(null)} history={history} />}
      {activeMenu === "results" && <ResultsPanel onClose={() => onMenuClick?.(null)} history={history} />}
    </div>
  );
}
